import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { COMPANY } from "@/data/content";
import { toast } from "sonner";
import {
  Plus, FileText, Plane, MapPin, ArrowRight, Smartphone, Building2, Send, Loader2, CreditCard, Upload,
} from "lucide-react";

function PaymentInfo() {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="p-6 rounded-2xl bg-brand-surface border border-white/10">
        <div className="flex items-center gap-3 mb-3"><Smartphone className="w-5 h-5 text-brand-gold" /><span className="font-display text-lg">Mobile Money</span></div>
        <p className="text-white/50 text-sm">Account Number</p>
        <p className="text-2xl font-display text-brand-gold tracking-wide" data-testid="momo-number">{COMPANY.momo}</p>
      </div>
      {COMPANY.banks.map((b, i) => (
        <div key={b.account} className="p-6 rounded-2xl bg-brand-surface border border-white/10">
          <div className="flex items-center gap-3 mb-3"><Building2 className="w-5 h-5 text-brand-gold" /><span className="font-display text-lg">Bank Transfer</span></div>
          <p className="text-white/50 text-sm">{b.name}</p>
          <p className="text-2xl font-display text-brand-gold tracking-wide" data-testid={`bank-number-${i}`}>{b.account}</p>
        </div>
      ))}
    </div>
  );
}

function PaymentSubmit({ applications, onDone }) {
  const [form, setForm] = useState({ method: "Mobile Money", amount: "", reference: "", application_id: "", note: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await api.post("/payments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Payment submitted — we'll verify it shortly.");
      setForm({ method: "Mobile Money", amount: "", reference: "", application_id: "", note: "" });
      setFile(null);
      onDone();
    } catch (err) {
      toast.error("Could not submit payment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-6 rounded-2xl bg-brand-surface border border-white/10 space-y-4" data-testid="payment-form">
      <h3 className="font-display text-lg flex items-center gap-2"><CreditCard className="w-5 h-5 text-brand-gold" /> Submit Payment Proof</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="payment-method-select"
          className="h-11 rounded-md bg-brand-navy border border-white/10 text-white px-3 text-sm">
          <option>Mobile Money</option>
          <option>Bank Transfer</option>
        </select>
        <Input placeholder="Amount (GHS)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required
          data-testid="payment-amount-input" className="bg-brand-navy border-white/10 text-white h-11" />
      </div>
      <Input placeholder="Transaction reference / ID" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
        data-testid="payment-reference-input" className="bg-brand-navy border-white/10 text-white h-11" />
      <select value={form.application_id} onChange={(e) => setForm({ ...form, application_id: e.target.value })} data-testid="payment-application-select"
        className="w-full h-11 rounded-md bg-brand-navy border border-white/10 text-white px-3 text-sm">
        <option value="">Link to application (optional)</option>
        {applications.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
      </select>
      <label className="flex items-center gap-3 p-3 rounded-md bg-brand-navy border border-dashed border-white/15 cursor-pointer text-sm text-white/60 hover:border-brand-gold/40">
        <Upload className="w-4 h-4 text-brand-gold" />
        {file ? file.name : "Attach payment screenshot (optional)"}
        <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files[0])} data-testid="payment-file-input" />
      </label>
      <Button type="submit" disabled={loading} data-testid="payment-submit-btn" className="rounded-full bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Payment"}
      </Button>
    </form>
  );
}

function Messages() {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/messages");
    setMsgs(data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setLoading(true);
    try {
      await api.post("/messages", { body });
      setBody("");
      await load();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-brand-surface border border-white/10 flex flex-col h-[520px]" data-testid="messages-panel">
      <div className="p-4 border-b border-white/10 font-display text-lg">Messages with Fragua Team</div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {msgs.length === 0 && <p className="text-white/40 text-sm text-center mt-8">No messages yet. Say hello 👋</p>}
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.sender_role === "customer" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${m.sender_role === "customer" ? "bg-brand-gold text-brand-navy rounded-br-sm" : "bg-brand-navy border border-white/10 text-white/90 rounded-bl-sm"}`}>
              {m.sender_role === "admin" && <p className="text-brand-gold text-xs mb-0.5 font-semibold">{m.sender_name || "Fragua Team"}</p>}
              {m.body}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
        <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a message…" data-testid="message-input"
          className="bg-brand-navy border-white/10 text-white" />
        <Button type="submit" disabled={loading} data-testid="message-send-btn" className="bg-brand-gold text-brand-navy hover:bg-brand-goldhover">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [a, p] = await Promise.all([api.get("/applications"), api.get("/payments")]);
    setApps(a.data);
    setPayments(p.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-brand-navy text-brand-text">
      <Navbar variant="app" />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 pt-28 pb-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-brand-gold text-xs tracking-[0.25em] uppercase mb-2">Welcome back</p>
            <h1 className="font-display text-4xl tracking-tight">{user?.name}</h1>
          </div>
          <Button onClick={() => navigate("/applications/new")} data-testid="new-application-btn"
            className="rounded-full bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold">
            <Plus className="w-4 h-4 mr-2" /> New Application
          </Button>
        </div>

        <Tabs defaultValue="applications">
          <TabsList className="bg-brand-surface border border-white/10 mb-8">
            <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="applications">
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-brand-gold animate-spin" /></div>
            ) : apps.length === 0 ? (
              <div className="text-center py-20 rounded-2xl bg-brand-surface/50 border border-white/10">
                <FileText className="w-10 h-10 text-brand-gold mx-auto mb-4" />
                <p className="text-white/60 mb-5">You have no applications yet.</p>
                <Button onClick={() => navigate("/applications/new")} className="rounded-full bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold">Start an Application</Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                {apps.map((a) => (
                  <button key={a.id} onClick={() => navigate(`/applications/${a.id}`)} data-testid={`application-card-${a.id}`}
                    className="text-left p-6 rounded-2xl bg-brand-surface border border-white/10 hover:border-brand-gold/40 hover:-translate-y-1 transition-all group">
                    <div className="flex items-center justify-between mb-4">
                      <span className="flex items-center gap-2 text-brand-gold">
                        {a.service_type === "visa" ? <FileText className="w-5 h-5" /> : <Plane className="w-5 h-5" />}
                        <span className="text-xs uppercase tracking-wide">{a.service_type === "visa" ? "Visa" : "Tour"}</span>
                      </span>
                      <StatusBadge status={a.status} testid={`application-status-${a.id}`} />
                    </div>
                    <h3 className="font-display text-xl mb-2">{a.title}</h3>
                    <p className="text-white/50 text-sm flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {a.destination}</p>
                    <div className="flex items-center text-brand-gold text-sm mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      View details <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="payments" className="space-y-6">
            <PaymentInfo />
            <PaymentSubmit applications={apps} onDone={load} />
            <div className="rounded-2xl bg-brand-surface border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10 font-display text-lg">Payment History</div>
              {payments.length === 0 ? (
                <p className="text-white/40 text-sm p-6 text-center">No payments submitted yet.</p>
              ) : (
                <div className="divide-y divide-white/5">
                  {payments.map((p) => (
                    <div key={p.id} className="p-4 flex items-center justify-between" data-testid={`payment-row-${p.id}`}>
                      <div>
                        <p className="font-medium">GHS {p.amount} <span className="text-white/40 text-sm">· {p.method}</span></p>
                        <p className="text-white/40 text-xs">{p.reference || "No reference"} · {new Date(p.created_at).toLocaleDateString()}</p>
                      </div>
                      <StatusBadge status={p.status} testid={`payment-status-${p.id}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <Messages />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
